import React, { Component } from 'react';
import { action, reaction } from 'mobx';
import { isEqual } from 'lodash';
import { withTheme } from 'styled-components';
import classNames from 'classnames';
import { Classes } from '@blueprintjs/core';
import { Flex, Icon, Tooltip } from 'core/components';
import { FieldLabel } from 'core/form/components/FieldLabel';
import FormGroup from './FormGroup';
import formConsumer from '../formConsumer';

/**
 * This doesn't really work properly as these fields often fail equality comparisons erroneously. It's also
 * just not really a good idea. But we're kind of stuck with it.
 */
function updateFieldStateWithProps(field, props) {
  if (field) {
    const { rules, placeholder, options, label, validateOptions, disabled, readOnly } = props;

    if (rules !== undefined && field.rules !== rules) {
      field.form.setRules(field, rules);
    }

    if (placeholder !== undefined && field.placeholder !== placeholder) {
      field.placeholder = placeholder;
    }

    if (validateOptions !== undefined && field.validateOptions !== validateOptions) {
      field.validateOptions = validateOptions;
    }

    if (options !== undefined && !isEqual(field.options, options)) {
      field.options = options;
      field.form.validateWithDefer();
    }

    if (label && field.label !== label) {
      field.label = label;
    }

    if (disabled !== undefined && field.disabled !== disabled) {
      field.disabled = disabled;
    }

    if (readOnly !== undefined && field.readOnly !== readOnly) {
      field.readOnly = readOnly;
    }
  }
}

@formConsumer
@withTheme
class Field extends Component {
  state = {};

  static defaultProps = {
    showLabel: true,
    small: true
  };

  @action
  static getDerivedStateFromProps(nextProps) {
    const { form, name } = nextProps;
    const field = nextProps.field || form.getField(name);
    if (!field) {
      console.warn(`Field MISSING in getDerivedStateFromProps w name: ${name}`);
    }
    updateFieldStateWithProps(field, nextProps);

    return { field };
  }

  /**
   * This serves as a hacky alternative to @observer, allowing us to respond to mobx
   * changes without subscribing to all mobx changes. This is not ideal but using mobx
   * properly results in infinite update loops thanks to `updateFieldStateWithProps()`
   */
  componentDidMount() {
    const { field } = this.state;

    // eslint-disable-next-line react/no-unused-class-component-methods
    this.fieldErrorsDisposer = reaction(
      () => field.errorString,
      (fieldErrors) => {
        // eslint-disable-next-line react/no-unused-state
        this.setState({ fieldErrors });
      }
    );

    // eslint-disable-next-line react/no-unused-class-component-methods
    this.valueDisposer = reaction(
      () => field.value,
      (value) => {
        // eslint-disable-next-line react/no-unused-state
        this.setState({ value });
      }
    );

    // eslint-disable-next-line react/no-unused-class-component-methods
    this.validDisposer = reaction(
      () => field.valid,
      // eslint-disable-next-line react/no-unused-state
      (valid) => this.setState({ valid })
    );

    // eslint-disable-next-line react/no-unused-class-component-methods
    this.forceUpdateDisposer = reaction(
      () => field.forceUpdate,
      // eslint-disable-next-line react/no-unused-state
      (forceUpdate) => this.setState({ forceUpdate })
    );
  }

  componentWillUnmount() {
    ['fieldErrorsDisposer', 'validDisposer', 'valueDisposer', 'forceUpdateDisposer'].forEach(
      (fn) => this[fn] && this[fn]()
    );
  }

  handleChange = (e) => {
    const { form, onChange, beforeOnChange } = this.props;
    const { field } = this.state;
    let interruptChange = false;

    if (e && e.stopPropagation) {
      e.stopPropagation(); // need to prevent bubbling!
    }

    const previousValue = field.getValue();

    if (beforeOnChange) {
      // we can potentially short-circuit the change from happening, if beforeOnChange passed in tells us so
      interruptChange = beforeOnChange(field, field?.value, previousValue, form, e.target?.value ?? e);
    }

    if (!interruptChange) {
      field.onChange(e);

      if (onChange) {
        onChange(field, field.value, previousValue, form);
      }
    }
  };

  handleKeyPress = (e) => {
    const { onEnterKey } = this.props;
    const { field } = this.state;

    if (e && e.key === 'Enter' && onEnterKey) {
      onEnterKey(field);
    }
  };

  render() {
    const {
      form,
      render,
      component,
      autoFocus,
      children,
      options,
      onChange,
      intent,
      isEditing,
      toggleEditing,
      showLabel,
      labelInfo,
      labelProps,
      small,
      large,
      helpText: helpTextProp,
      tooltip,
      fieldStyle,
      onQuery,
      showRequired,
      showError: showErrorProp,
      loading,
      isOpen,
      className,
      theme,
      subLabel: subLabelProp,
      ...formGroupProps
    } = this.props;

    const { field } = this.state;
    if (!field) {
      console.error('Field MISSING in render', field);
      // we might be better off returning null istead of the component crashing when the field is missing but I am not sure of the side effects.
      // return null;
    }
    const {
      name,
      label,
      placeholder,
      value,
      disabled,
      helpText,
      errors,
      hasError,
      showPristineErrors,
      pristine,
      readOnly,
      rules,
      subLabel,
      inputRef
    } = field;

    const showError = hasError && (showPristineErrors || !pristine);
    const smallField = form.options.large ? false : small && !large;
    const labelId = `label-${field._id}`;
    const helperTextId = `helperText-${field._id}`;

    const isRequired = rules.toString().includes('required');
    const showRequiredLabel = showRequired === undefined ? isRequired : showRequired;

    let fieldProps = {
      autoFocus,
      disabled,
      form,
      field,
      helperTextId,
      id: field._id, // note: name isn't guaranteed unique across all forms
      intent: intent || (showError ? 'danger' : undefined),
      onChange: this.handleChange,
      onKeyPress: this.handleKeyPress,
      isEditing,
      onEditComplete: toggleEditing,
      name,
      options,
      placeholder,
      value,
      small: smallField,
      large: !smallField,
      onQuery,
      loading,
      isOpen,
      readOnly,
      inputRef,
      isRequired
    };

    if (showLabel) {
      fieldProps.labelId = labelId;
    }

    // allow more generic usage of `Field`
    if (typeof children === 'function') {
      return children({
        ...fieldProps
      });
    }

    const possibleError = (
      <span id={helperTextId} role="alert" aria-live="assertive">
        {errors.map((error) => (
          <span key={error}>{error}</span>
        ))}
      </span>
    );
    const possibleHelperText = (helpTextProp || helpText) && (
      <span id={helperTextId} aria-live="assertive">
        {helpTextProp || helpText}
      </span>
    );

    const helperText = showErrorProp !== false && showError ? possibleError : possibleHelperText;

    if (render) {
      return render({ ...fieldProps });
    }

    let InputComponent;

    if (component) {
      InputComponent = component;
    }

    const formGroupAriaProps = {};
    if (fieldProps.disabled) {
      formGroupAriaProps['aria-disabled'] = true;
    }
    formGroupAriaProps['aria-required'] = isRequired;
    formGroupAriaProps.ariaRequired = isRequired;
    fieldProps = { ...fieldProps, ...formGroupAriaProps };

    // if InputRef is defined then the one passed from FieldState will not apply
    // PS - we might want to do that with some or all of children.props?
    if (children?.props?.inputRef) {
      fieldProps.inputRef = children.props.inputRef;
    }
    return (
      <FormGroup
        {...formGroupAriaProps}
        labelFor={fieldProps.id}
        labelId={labelId}
        labelText={label}
        helperText={helperText}
        tooltip={tooltip}
        intent={showError ? 'danger' : 'none'}
        labelInfo={
          <Flex display="inline-flex" alignItems="center">
            {showRequiredLabel ? '*' : ''}
            {labelInfo}
            {tooltip && (
              <Tooltip content={tooltip}>
                <Icon iconSize={14} icon="info-sign" color={theme.colors.gray3} mt={0} ml={1} />
              </Tooltip>
            )}
          </Flex>
        }
        style={fieldStyle}
        subLabel={subLabelProp || subLabel}
        {...formGroupProps}
        className={classNames({ [Classes.SMALL]: smallField }, className)}
        label={
          showLabel && label ? (
            <FieldLabel
              labelId={labelId}
              smallField={smallField}
              showError={showError}
              inline={formGroupProps.inline}
              {...labelProps}
            >
              {label}
            </FieldLabel>
          ) : null
        }
      >
        {children && React.cloneElement(children, fieldProps)}
        {component && <InputComponent {...fieldProps} />}
      </FormGroup>
    );
  }
}

export default Field;
